import {
  AfterViewInit,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
  input
} from '@angular/core';
import { OrgChartModalComponent, OrgChartModalData } from '../org-chart-modal/org-chart-modal.component';
import { Dialog } from '@angular/cdk/dialog';
import { OrgChartItem } from '../interfaces/org-chart-item';
import { MultiSelectComponent, MultiSelectModule } from '@progress/kendo-angular-dropdowns';
import { IconComponent } from '../../../../shared/components/ui/icon/icon.component';
import { ReactiveFormsModule } from '@angular/forms';
import { OrganizationChartApiService } from '../../../../api/bizzmine/organization-chart/organization-chart-api.service';
import { OrganizationChartItemType } from '../../../../../models/ts/organization-chart-item-type.model';
import { debounceTime, distinctUntilChanged, exhaustMap, of, Subject, take } from 'rxjs';
import { UpdateDeleteState } from 'src/models/ts/update-delete-state.model';
import { CollectionFormValidatorError } from '../../form/classes/collection-form-validator-error';
import { DepartmentType } from '../../../../../models/ts/department-type';
import { TranslatePipe } from 'src/app/shared/pipes/translate/translate.pipe';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';

@Component({
  selector: 'bizz-org-chart-select',
  standalone: true,
  imports: [
    MultiSelectModule,
    IconComponent,
    ReactiveFormsModule,
    TranslatePipe
  ],
  templateUrl: './org-chart-select.component.html',
  styleUrl: './org-chart-select.component.scss'
})
export class OrgChartSelectComponent implements OnInit, AfterViewInit {

  @Input() public errorState: CollectionFormValidatorError | undefined;
  @Input() public readonly = false;
  @Input() public allowedItemTypes: Array<OrganizationChartItemType> = [OrganizationChartItemType.User, OrganizationChartItemType.CurrentUser, OrganizationChartItemType.Department, OrganizationChartItemType.Function];
  public excludeExternalUsers = input<boolean | undefined>(undefined);
  @Input({ required: true })
  public set selection(values: Array<OrgChartItem>) {
    const orgItems: Array<OrgChartItem> = [];
    if (values) {
      values.forEach(item => {
        if (orgItems.find(e => e.UniqueID === item.UniqueID) == null && item.UpdateState !== UpdateDeleteState.Delete) {
          orgItems.push(structuredClone(item));
        }
      });
    }
    this._selection = orgItems;
  }

  private _selection: Array<OrgChartItem> = [];

  public get selection(): Array<OrgChartItem> {
    return this._selection;
  }

  @Output() public selectionChanged = new EventEmitter<Array<OrgChartItem>>();
  @Output() public selectionWithAllStatesChanged = new EventEmitter<Array<OrgChartItem>>();
  @Input() public min: number;
  @Input() public max: number;
  @Input() public excludedUserIds: Array<number> = [];
  public selectionWithAllStates: Array<OrgChartItem> = [];
  @Input() public rootNodeType: OrganizationChartItemType;
  @Input() public rootNodeId: number;
  @ViewChild('input') public kendoMultiSelect: MultiSelectComponent;
  public readonly DepartmentType = DepartmentType;
  public lookupResults: Array<OrgChartItem> = [];
  public lookup$ = new Subject<string>();
  private dialog = inject(Dialog);
  private orgChartApiService = inject(OrganizationChartApiService);
  public isFocused: boolean = false;
  public inputId: any = 'multiselect_' + crypto.randomUUID();
  public kendoElement: HTMLElement;

  private documentEventsService = inject(DocumentEventsService);
  private ngZone: NgZone = inject(NgZone);
  private destroyRef: DestroyRef = inject(DestroyRef);

  public constructor() {
    this.lookup$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      exhaustMap(search => {
          if (search.length > 0)
            return this.orgChartApiService.searchOrgChart(search, this.allowedItemTypes, this.excludedUserIds).pipe(take(1));
          else
            return of(null);
        }
      )
    ).subscribe({
      next: result => {
        if (result) {
          this.kendoMultiSelect.toggle(true);
          this.lookupResults = result.filter(item => this.selection.find(s => s.UniqueID === item.UniqueID) == null);
        } else {
          this.kendoMultiSelect.toggle(false);
        }
      },
      error: error => {
        this.kendoMultiSelect.toggle(true);
        this.lookupResults = [];
      }
    });

  }

  protected readonly OrganizationChartItemType = OrganizationChartItemType;

  public ngOnInit(): void {

    this.selectionWithAllStates = structuredClone(this.selection);

    this.documentEventsService.documentClickEvent.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (event: Event) => {
          if (event?.target && this.checkHandleClick(event.target)) {
            this.ngZone.run(() => {
              this.onDocumentClick(event);
            });
          }
        });
  }

  public checkHandleClick(target: EventTarget): boolean {
    const clickedOnEl = this.kendoElement.contains(target as Node);
    return !this.readonly &&
      ((!clickedOnEl && this.isFocused) ||
        (clickedOnEl && !this.isFocused));
  }

  public onDocumentClick(event: Event): void {
    if (!this.kendoElement.contains(event.target as Node)) {
      this.isFocused = false;
    } else {
      this.isFocused = true;
      setTimeout(() => {
        this.kendoElement.getElementsByTagName('input')[0].focus();
      }, 1);
      if (event) event.stopImmediatePropagation();
    }
  }

  public ngAfterViewInit(): void {
    this.kendoElement = document.getElementById(this.inputId) as HTMLElement;
  }

  public onOpen(event: any): void {
    event.preventDefault();
  }

  public selectionChange($event: OrgChartItem[]): void {
    this.selection = structuredClone($event);
    //TODO: IF NEW item of selection PUSH IN selectionWithAllStates
    this.selection.forEach(item => {
      if (this.selectionWithAllStates.find(s => s.UniqueID === item.UniqueID) == null) {
        item = structuredClone(item);
        item.UpdateState = UpdateDeleteState.Update;
        this.selectionWithAllStates.push(item);
      }
    });

    //Set removed items to delete
    this.selectionWithAllStates = this.selectionWithAllStates.map(item => {
      if (this.selection.find(s => s.UniqueID === item.UniqueID) == null) {
        item.UpdateState = UpdateDeleteState.Delete;
      } else {
        item.UpdateState = UpdateDeleteState.Update;
      }
      return item;
    });
    this.selectionWithAllStatesChanged.emit(this.selectionWithAllStates);
    this.selectionChanged.emit(this.selection);

    //Warn if more than the max allowed items are selected.
    if ($event.length) {
      this.errorState = {
        Message: ''
      };
      if (this.max > 0 && $event && $event.length > this.max) {
        this.errorState.Message = 'Provide a value equal or lower than ' + this.max;
      } else if (this.min > 0 && $event && $event.length < this.min) {
        this.errorState.Message = 'Provide a value equal or higher than ' + this.min;
      } else {
        this.errorState = undefined;
      }
    }
  }

  public openOrgChart(): void {
    const dialogRef = this.dialog.open<Array<OrgChartItem>, Partial<OrgChartModalData>>(OrgChartModalComponent, {
      data:
        {
          items: this.selection,
          min: this.min,
          max: this.max,
          allowedItemTypes: this.allowedItemTypes,
          excludedUserIds: this.excludedUserIds,
          excludeExternalUsers:  this.excludeExternalUsers() ?? true,
          rootNodeType: this.rootNodeType,
          rootNodeId: this.rootNodeId
        }
    });
    dialogRef.closed.pipe(take(1)).subscribe({
      next: (result) => {
        if (result) {
          this.selectionChange(result);
        } else {
          this.selectionChanged.emit(result);
        }
      }
    });
  }

  public onTabEvent(event: Event): void {
    event.preventDefault();
    const activeItem = document.querySelector('.k-list-item.k-focus') as HTMLElement;
    if (activeItem) {
      activeItem.click();
    }
  }

  public focus(): void {
    this.kendoMultiSelect.focus();
  }

}
